import React, {useMemo, useState} from "react";
import {Table} from "react-bootstrap";
import SortableTh from "../SortableTh.jsx";
import HelpTooltip from "../HelpTooltip.jsx";
import {formatEuro, formatFarbnummer, formatQuantity, produktlinieLabel} from "./format.js";

const GRUPPIERUNGEN = [
    {id: "farbton", label: "Je Farbton"},
    {id: "produktlinie", label: "Je Produktlinie"},
];

const SPALTEN_FARBTON = [
    {key: "farbname", label: "Farbton"},
    {key: "produktlinie", label: "Produktlinie"},
    {key: "menge_entgangen", label: "Menge entgangen"},
    {key: "umsatz_entgangen", label: "Umsatz entgangen"},
];

const SPALTEN_PRODUKTLINIE = [
    {key: "produktlinie", label: "Produktlinie"},
    {key: "menge_entgangen", label: "Menge entgangen"},
    {key: "umsatz_entgangen", label: "Umsatz entgangen"},
];

function sortiere(zeilen, sortKey, sortDir) {
    const faktor = sortDir === "asc" ? 1 : -1;
    return [...zeilen].sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        if (typeof av === "number" && typeof bv === "number") {
            return (av - bv) * faktor;
        }
        return String(av ?? "").localeCompare(String(bv ?? ""), "de") * faktor;
    });
}

/**
 * Aufschlüsselung je Farbton bzw. je Produktlinie mit Summenzeile
 * (Freigabe 22.09.2026, Punkt 5: "Aufschlüsselung je Farbton und je
 * Produktlinie, sortierbar, mit Summe"). Die Gruppierung ist ein Umschalter
 * (eigene Schaltflächen, kein natives <select>), die Sortierung läuft über
 * den hauseigenen SortableTh-Kopf.
 */
export default function EntgangenerUmsatzTabelle({zeilenFarbton, zeilenProduktlinie, summe}) {
    const [gruppierung, setGruppierung] = useState("farbton");
    const [sortKey, setSortKey] = useState("umsatz_entgangen");
    const [sortDir, setSortDir] = useState("desc");

    const quelle = gruppierung === "farbton" ? (zeilenFarbton || []) : (zeilenProduktlinie || []);
    const spalten = gruppierung === "farbton" ? SPALTEN_FARBTON : SPALTEN_PRODUKTLINIE;

    const sortierteZeilen = useMemo(() => sortiere(quelle, sortKey, sortDir), [quelle, sortKey, sortDir]);

    const onSort = (key) => {
        if (key === sortKey) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("desc");
        }
    };

    if (quelle.length === 0) {
        return null;
    }

    return (
        <div className="card shadow-sm mb-3">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                <span className="fw-semibold d-inline-flex align-items-center gap-1">
                    Aufschlüsselung
                    <HelpTooltip placement="right" size={0.8}>
                        Jede Zeile summiert den geschätzten entgangenen Umsatz des gewählten Zeitraums für genau
                        diesen Farbton bzw. diese Produktlinie. Spaltenköpfe sind klickbar und sortieren die Liste.
                    </HelpTooltip>
                </span>
                <div className="d-inline-flex align-items-stretch p-1 rounded-pill bg-body-secondary" role="group" aria-label="Gruppierung">
                    {GRUPPIERUNGEN.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            aria-pressed={gruppierung === item.id}
                            className={`btn btn-sm rounded-pill border-0 ${gruppierung === item.id ? "bg-white text-body shadow-sm fw-semibold" : "text-body"}`}
                            style={{padding: ".25rem .8rem"}}
                            onClick={() => setGruppierung(item.id)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="card-body p-0">
                <Table responsive hover size="sm" className="mb-0 align-middle">
                    <thead>
                        <tr>
                            {spalten.map((spalte) => (
                                <SortableTh key={spalte.key} column={spalte} sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortierteZeilen.map((zeile) => (
                            <tr key={zeile.artikel_nummer || zeile.produktlinie}>
                                {gruppierung === "farbton" && (
                                    <td>
                                        <span className="d-inline-flex align-items-center gap-2">
                                            {zeile.hex_color && (
                                                <span
                                                    aria-hidden="true"
                                                    style={{
                                                        display: "inline-block",
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: "50%",
                                                        backgroundColor: zeile.hex_color,
                                                        border: "1px solid rgba(0,0,0,.15)",
                                                    }}
                                                />
                                            )}
                                            {zeile.farbnummer !== null && zeile.farbnummer !== undefined
                                                ? `${formatFarbnummer(zeile.farbnummer, zeile.farbnummer_unter)} `
                                                : ""}
                                            {zeile.farbname}
                                        </span>
                                    </td>
                                )}
                                <td>{produktlinieLabel(zeile.produktlinie)}</td>
                                <td>{formatQuantity(zeile.menge_entgangen)} Stk.</td>
                                <td>
                                    {zeile.umsatz_entgangen === null || zeile.umsatz_entgangen === undefined
                                        ? <span className="text-muted">kein Preis hinterlegt</span>
                                        : formatEuro(zeile.umsatz_entgangen)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="fw-semibold">
                            <td colSpan={gruppierung === "farbton" ? 2 : 1}>Summe</td>
                            <td>{formatQuantity(summe?.menge_entgangen ?? 0)} Stk.</td>
                            <td>{formatEuro(summe?.umsatz_entgangen ?? 0)}</td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
}
